import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DialogEnums, StyleEnums } from "models/enums";
import { SnackbarKey } from "notistack";
import { ForwardableCost } from "models/utilTypes/costForwardingTypes";
import { ForwardedCostsSelection } from "models/utilTypes/forwardableCostTypes";
import type {
  ForwardedCostAttachments,
  ForwardedCostLinkKey,
  ForwardedCostRowContext,
  MissingForwardedCostDocument,
} from "utils/helpers/forwardedCostHelper";
import { WintFormSetFieldValue, WintFormSetValues } from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { useCheckRelations, useFloatingMessages } from "components/common";
import { useCostForwardingService, useCurrencyService, useImageService, useInvoiceService } from "serviceHooks";
import ForwardedCostsDialogForm from "./forwardedCostsDialogForm";
import { fetchForwardedCostFiles, getRatesForNewRows, loadForwardableCosts } from "./forwardedCostsRequests";
//#endregion
import { ForwardedCostHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

/**
 * Loads the selected customer's forwardable costs and turns the dialog's selection into invoice rows. On a customer
 * switch the previous customer's rows go and come back when that customer is chosen again.
 */
function useForwardedCosts(
  values: InvoiceFormTypes.Create,
  setValues: WintFormSetValues<InvoiceFormTypes.Create>,
  setFieldValue: WintFormSetFieldValue<InvoiceFormTypes.Create>,
  acceptsForwardedCosts: boolean
) {
  const dispatch = useDispatch();
  const costForwardingService = useCostForwardingService();
  const currencyService = useCurrencyService();
  const invoiceService = useInvoiceService();
  const imageService = useImageService();
  const { enqueueMessage, closeMessage } = useFloatingMessages();
  const { getIsReadonlyUser } = useCheckRelations();
  const isReadonlyUser = getIsReadonlyUser();
  const isOffered = acceptsForwardedCosts && !isReadonlyUser;

  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);
  const invoiceDimensions = useSelector((store: AppState) => store.common.dimensions.invoiceDimensions);

  // The dialog's callbacks outlive the render that opened it; they read the form through this ref.
  const latestValuesRef = React.useRef(values);
  latestValuesRef.current = values;

  const customerId = values.customer?.id ? Number(values.customer.id) : undefined;
  const [costs, setCosts] = React.useState<ForwardableCost[]>([]);
  const [dismissedCustomerIds, setDismissedCustomerIds] = React.useState<number[]>([]);

  const dismissReminder = () => {
    if (customerId !== undefined) setDismissedCustomerIds((ids) => [...ids, customerId]);
  };

  React.useEffect(() => {
    setCosts([]);
    if (!isOffered || customerId === undefined) return;
    return loadForwardableCosts(customerId, costForwardingService.getForwardableCosts, async (loaded, isCurrent) => {
      await restoreRemovedCosts(customerId, loaded, isCurrent);
      // Listed only after the restore, so the reminder does not count the costs that are on their way back.
      if (isCurrent()) setCosts(loaded);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isOffered]);

  // The form's updater gets the values of the last render, so results finishing before a re-render are applied
  // together, in one update, or all but the last would be lost.
  const finishedAttachmentsRef = React.useRef<
    { customerId: number; key: ForwardedCostLinkKey; attachments: ForwardedCostAttachments }[]
  >([]);
  // Results queued when the cost rows are removed below would be applied on the values of the last render, which
  // still hold the rows, and undo the removal.
  const dropFinishedAttachments = () => {
    finishedAttachmentsRef.current = [];
  };

  React.useEffect(() => {
    if (!ForwardedCostHelper.hasOtherCustomersForwardedCosts(latestValuesRef.current)) return;
    dropFinishedAttachments();
    setValues((prev) => ForwardedCostHelper.removeOtherCustomersForwardedCosts(prev), true);
    enqueueMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsRemovedForCustomerChange),
      StyleEnums.DisplayType.Info,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Switching a new invoice to Återkommande keeps the form; a recurring invoice reserves nothing, so the
  // cost rows would be billed on every generated invoice while the costs stayed open.
  React.useEffect(() => {
    if (acceptsForwardedCosts || ForwardedCostHelper.getForwardedCostIds(latestValuesRef.current).length === 0) return;
    dropFinishedAttachments();
    setValues((prev) => ForwardedCostHelper.removeForwardedCosts(prev), true);
    enqueueMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsRemovedForRecurring),
      StyleEnums.DisplayType.Info,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptsForwardedCosts]);

  const defaultUnitIdRef = React.useRef<Promise<number>>();
  const getDefaultUnitId = () => {
    if (!defaultUnitIdRef.current) {
      defaultUnitIdRef.current = invoiceService
        .getUnits()
        .then((units) => units.find((u) => u.name === InvoiceConstants.DefaultUnit)?.id ?? 0)
        .catch(() => 0);
    }
    return defaultUnitIdRef.current;
  };

  const fallbackVatRate = noVatCompany ? 0 : InvoiceConstants.DefaultVatType;
  const formatVatText = (vatRate: number) =>
    vatRate === 0 ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree) : `${vatRate}%`;

  const getRecommendedDimensions = () => {
    const allowedTypes = invoiceService.getCompatibleDimensionTypes(false);
    return (invoiceDimensions ?? [])
      .filter((d) => allowedTypes.includes(d.key.type))
      .map((d) => d.key.recommendedDimensionId)
      .filter((d): d is string => !!d);
  };

  const getRates = async (selection: ForwardedCostsSelection) => {
    const current = latestValuesRef.current;
    try {
      return await getRatesForNewRows(
        selection,
        {
          currency: current.currency,
          invoiceDate: current.otherSettings.invoiceDate,
          addedCostIds: ForwardedCostHelper.getForwardedCostIds(current),
        },
        { getExchangeRate: currencyService.getExchangeRate, getDefaultUnitId }
      );
    } catch (error) {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong),
        StyleEnums.DisplayType.Danger
      );
      throw error;
    }
  };

  const getRowContext = (rates: { exchangeRate: number; unitId: number }) => {
    const recommendedDimensions = getRecommendedDimensions();
    return (prev: InvoiceFormTypes.Create): ForwardedCostRowContext => ({
      exchangeRate: rates.exchangeRate,
      fallbackVatRate,
      formatVatText,
      createBaseRow: (): InvoiceFormTypes.Product => ({
        uniqueId: UtilsHelper.getUniqueId(),
        id: 0,
        price: 0,
        quantity: 1,
        tax: fallbackVatRate,
        taxText: formatVatText(fallbackVatRate),
        unit: rates.unitId,
        eUVatCategoryId: WintSuperkollAPIModel.EuVatProductType.NotDefined,
        periodisation: { mode: prev.isPeriodisationByDate ? "date" : "month" },
        dimensions: recommendedDimensions,
        isEUCustomer: prev.isEUCustomer,
      }),
    });
  };

  // Keyed by the link's row, so confirming the dialog again while a cost's files are on their way fetches them once.
  const pendingAttachmentsRef = React.useRef(
    new Map<InvoiceFormTypes.ForwardedCostLink["rowUniqueId"], Promise<void>>()
  );
  // The links a selection adds exist only once the form has rendered them, so their files are fetched from an effect.
  const costsToAttachRef = React.useRef<ForwardableCost[]>();
  const [finishedAttachmentsCount, setFinishedAttachmentsCount] = React.useState(0);
  const [pendingAttachments, setPendingAttachments] = React.useState(0);
  const attachmentWaitersRef = React.useRef<(() => void)[]>([]);

  const releaseAttachmentWaiters = () => {
    if (
      costsToAttachRef.current !== undefined ||
      pendingAttachmentsRef.current.size > 0 ||
      finishedAttachmentsRef.current.length > 0
    )
      return;
    const waiters = attachmentWaitersRef.current;
    attachmentWaitersRef.current = [];
    waiters.forEach((release) => release());
  };

  // A save reads the values after this resolves, so a cost's files must be in the rendered values by then, not
  // merely downloaded.
  const waitForAttachments = () =>
    new Promise<void>((resolve) => {
      attachmentWaitersRef.current.push(resolve);
      releaseAttachmentWaiters();
    });

  const applySelection = async (selection: ForwardedCostsSelection, listedCosts: ForwardableCost[]) => {
    const rowContext = getRowContext(await getRates(selection));
    costsToAttachRef.current = listedCosts;
    setValues(
      (prev) => ForwardedCostHelper.applyForwardedCostSelection(prev, listedCosts, selection, rowContext(prev)),
      true
    );
  };

  const attachCostFiles = (listedCosts: ForwardableCost[]) => {
    const current = latestValuesRef.current;
    const pending = pendingAttachmentsRef.current;
    if (customerId === undefined) return;

    ForwardedCostHelper.getLinksToFetch(current).forEach((link) => {
      const cost = listedCosts.find((c) => c.id === link.costId);
      if (!cost || pending.has(link.rowUniqueId)) return;

      const key = { costId: link.costId, rowUniqueId: link.rowUniqueId };
      const attaching = fetchForwardedCostFiles(cost, {
        getImagesMeta: imageService.getImagesMeta,
        getImageFile: imageService.getImageFile,
      })
        .catch(() => ({ files: [], missing: "notAttached" as const }))
        .then(({ files, missing }) => {
          pending.delete(link.rowUniqueId);
          setPendingAttachments(pending.size);
          finishedAttachmentsRef.current.push({
            customerId,
            key,
            attachments: { files, missingDocument: missing ? cost.sourceDocument : undefined, missingReason: missing },
          });
          setFinishedAttachmentsCount((count) => count + 1);
        });
      pending.set(link.rowUniqueId, attaching);
    });
    setPendingAttachments(pending.size);
  };

  React.useEffect(() => {
    const listedCosts = costsToAttachRef.current;
    if (!listedCosts) return;
    costsToAttachRef.current = undefined;
    attachCostFiles(listedCosts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.forwardedCosts]);

  // Runs after every commit and before the effect below applies the finished results, so a waiter is released on
  // the render that holds them.
  React.useEffect(releaseAttachmentWaiters);

  React.useEffect(() => {
    const finished = finishedAttachmentsRef.current;
    if (finished.length === 0) return;
    finishedAttachmentsRef.current = [];
    // Each result is checked against the values it lands on, which drop it when its cost no longer takes files.
    const applyFinished = (prev: InvoiceFormTypes.Create) =>
      finished.reduce(
        (next, result) =>
          ForwardedCostHelper.addForwardedCostAttachments(next, result.customerId, result.key, result.attachments),
        prev
      );
    // An update with nothing to add would still hand back the rendered values and undo an update made in the same
    // commit, such as the customer switch removing the cost rows.
    const latest = latestValuesRef.current;
    if (applyFinished(latest) === latest) {
      releaseAttachmentWaiters();
      return;
    }
    setValues(applyFinished, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedAttachmentsCount]);

  // Set field by field: an updater gets the values of the last render and would undo what another step did on the same
  // row deletion, such as ROT/RUT dropping the row's article. A pending fetch for the row is dropped when it finishes.
  React.useEffect(() => {
    const latest = latestValuesRef.current;
    const next = ForwardedCostHelper.removeFilesOfDeletedRows(latest);
    if (next === latest) return;
    setFieldValue("forwardedCosts", next.forwardedCosts, false);
    setFieldValue("otherSettings.attachments", next.otherSettings.attachments, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.products]);

  // Rebuilt from a fresh list and freshly fetched rates through the dialog's apply, since a cost may have been
  // forwarded elsewhere or changed while another customer was chosen. Failing to fetch the rates keeps the selection.
  // The switch removed the costs' files, so they are fetched again as on a confirm.
  const restoreRemovedCosts = async (
    listedForCustomerId: number,
    listedCosts: ForwardableCost[],
    isCurrent: () => boolean
  ) => {
    const restore = ForwardedCostHelper.getForwardedCostsToRestore(latestValuesRef.current, listedCosts);
    if (!restore) return;
    const rates = await getRates(restore.selection).catch(() => undefined);
    if (!rates || !isCurrent()) return;

    const rowContext = getRowContext(rates);
    costsToAttachRef.current = listedCosts;
    setValues(
      (prev) => ForwardedCostHelper.restoreForwardedCosts(prev, listedForCustomerId, listedCosts, rowContext(prev)),
      true
    );
    if (restore.droppedCount > 0) {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsNotRestored, undefined, {
          count: restore.droppedCount,
        }),
        StyleEnums.DisplayType.Info,
        true
      );
    }
  };

  const openDialog = () => {
    const initialSelection = ForwardedCostHelper.getForwardedCostsSelection(latestValuesRef.current);
    const listedCosts = costs;

    dispatch(
      displayDialog({
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ForwardedCostsAdd),
        titleIcon: IconDefinitionConstants.WCostForward,
        dialogType: DialogEnums.DialogType.Content,
        maxWidth: "md",
        fullWidth: true,
        children: (
          <ForwardedCostsDialogForm
            costs={listedCosts}
            initialSelection={initialSelection}
            onApply={(selection) => applySelection(selection, listedCosts)}
          />
        ),
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(
              initialSelection.selectedCostIds.length > 0
                ? TranslationKeys.Common.SaveChanges
                : TranslationKeys.Common.Add
            ),
          },
        ],
      })
    );
  };

  const missingDocuments = ForwardedCostHelper.getMissingDocuments(values);
  const missingDocumentsKey = missingDocuments.map((missing) => missing.key).join();
  const missingDocumentMessagesRef = React.useRef(new Map<string, SnackbarKey>());

  // A warning stays until the user closes it or its cost leaves the invoice, and is shown once per cost.
  React.useEffect(() => {
    const shown = missingDocumentMessagesRef.current;
    const current = new Set(missingDocuments.map((missing) => missing.key));
    shown.forEach((messageKey, key) => {
      if (current.has(key)) return;
      closeMessage(messageKey);
      shown.delete(key);
    });
    missingDocuments
      .filter((missing) => !shown.has(missing.key))
      .forEach((missing) =>
        shown.set(
          missing.key,
          enqueueMessage(getMissingDocumentMessage(missing), StyleEnums.DisplayType.Warning, false)
        )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingDocumentsKey]);

  React.useEffect(() => {
    const shown = missingDocumentMessagesRef.current;
    return () => shown.forEach((messageKey) => closeMessage(messageKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const offer = ForwardedCostHelper.getCostForwardingOffer(costs, values, {
    acceptsForwardedCosts,
    isReadonlyUser,
    dismissedCustomerIds,
  });

  return {
    ...offer,
    customerName: values.customer?.value ?? "",
    openDialog,
    dismissReminder,
    pendingAttachments,
    waitForAttachments,
    getLatestValues: () => latestValuesRef.current,
  };
}

const getMissingDocumentMessage = ({ document, reason }: MissingForwardedCostDocument) =>
  LocalizationHelperInstance.getFormattedMessage(
    reason === "noAccess"
      ? TranslationKeys.Invoice.ForwardedCostDocumentNoAccess
      : TranslationKeys.Invoice.ForwardedCostDocumentNotAttached,
    undefined,
    { document }
  );

export default useForwardedCosts;
